/**
 * Admin Postal-Return Panel
 *
 * Shows the lifecycle of a postal-return for the current dispute item:
 *   pending_buyer_ship → shipped_by_buyer → received_by_seller (lock released)
 *                        ↓
 *                        rejected_by_seller (admin must intervene)
 *
 * If the seller doesn't respond OR the buyer presents valid tracking proof but
 * the seller refuses to confirm, the admin clicks "آزادسازی اجباری" here to
 * release the lock so the buyer can withdraw the refund.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ShipIcon from '@mui/icons-material/LocalShipping';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassIcon from '@mui/icons-material/HourglassEmpty';
import adminPostalReturnService, {
  AdminPostalReturnState,
  PostalReturnStatus
} from '../../services/postalReturn.service';

interface AdminPostalReturnPanelProps {
  orderItemId: string;
}

const STATUS_META: Record<PostalReturnStatus, {
  label: string;
  color: 'warning' | 'info' | 'success' | 'error' | 'default';
  icon: React.ReactNode;
  hint: string;
}> = {
  pending_buyer_ship: {
    label: 'در انتظار ارسال خریدار',
    color: 'warning',
    icon: <HourglassIcon fontSize="small" />,
    hint: 'خریدار باید محصول را برای فروشنده ارسال کند و کد رهگیری ثبت کند.'
  },
  shipped_by_buyer: {
    label: 'ارسال شد - در انتظار تأیید فروشنده',
    color: 'info',
    icon: <ShipIcon fontSize="small" />,
    hint: 'خریدار محصول را ارسال کرده. منتظر تأیید دریافت توسط فروشنده.'
  },
  received_by_seller: {
    label: 'تأیید شد - بازپرداخت آزاد',
    color: 'success',
    icon: <CheckIcon fontSize="small" />,
    hint: 'فروشنده دریافت را تأیید کرده و قفل بازپرداخت آزاد شده است.'
  },
  rejected_by_seller: {
    label: 'فروشنده رد کرد - نیاز به بررسی ادمین',
    color: 'error',
    icon: <CancelIcon fontSize="small" />,
    hint: 'فروشنده دریافت را رد کرده. ادمین باید بررسی کند و تصمیم نهایی بگیرد.'
  },
  timed_out: {
    label: 'مهلت منقضی شد',
    color: 'error',
    icon: <CancelIcon fontSize="small" />,
    hint: 'مهلت ارسال یا تأیید گذشته است.'
  }
};

const AdminPostalReturnPanel: React.FC<AdminPostalReturnPanelProps> = ({ orderItemId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<AdminPostalReturnState | null>(null);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceNotes, setForceNotes] = useState('');
  const [forcing, setForcing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminPostalReturnService.get(orderItemId);
      setState(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'خطا در دریافت اطلاعات مرجوعی');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderItemId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemId]);

  const handleForceRelease = async () => {
    setForcing(true);
    try {
      const updated = await adminPostalReturnService.forceRelease(orderItemId, forceNotes.trim() || undefined);
      setState(updated);
      setForceDialogOpen(false);
      setForceNotes('');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'خطا در آزادسازی اجباری');
    } finally {
      setForcing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // No postal-return record on this item — nothing to show. Only render the
  // panel when there's actually a return flow in progress.
  if (!state || !state.postalReturnInfo) return null;

  const ret = state.postalReturnInfo;
  const meta = STATUS_META[ret.status] ?? {
    label: String(ret.status ?? 'نامشخص'),
    color: 'default' as const,
    icon: <HourglassIcon fontSize="small" />,
    hint: 'وضعیت ناشناخته. لطفاً با تیم فنی تماس بگیرید.'
  };
  const isReleased = ret.status === 'received_by_seller';
  const fa = (n: number) => Number(n).toLocaleString('fa-IR');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        mt: 2,
        borderColor: isReleased ? 'success.main' : 'warning.main',
        bgcolor: isReleased ? 'rgba(46,125,50,0.04)' : 'rgba(237,108,2,0.04)'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        {isReleased ? (
          <LockOpenIcon color="success" fontSize="small" />
        ) : (
          <LockIcon color="warning" fontSize="small" />
        )}
        <Typography variant="subtitle1" fontWeight={700}>
          مرجوع کالای پستی
        </Typography>
        <Chip size="small" icon={meta.icon as any} label={meta.label} color={meta.color} sx={{ fontWeight: 600 }} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={0.75} sx={{ fontSize: '0.85rem' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            مبلغ بازپرداخت قفل‌شده در کیف خریدار:
          </Typography>{' '}
          <Typography variant="caption" fontWeight={700}>
            {fa(ret.refundAmount || 0)} تومان
          </Typography>
        </Box>

        {state.storeName && (
          <Box>
            <Typography variant="caption" color="text.secondary">فروشنده مقصد مرجوعی:</Typography>{' '}
            <Typography variant="caption">{state.storeName}</Typography>
          </Box>
        )}

        {ret.buyerTrackingCode && (
          <Box>
            <Typography variant="caption" color="text.secondary">کد رهگیری خریدار:</Typography>{' '}
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{ret.buyerTrackingCode}</Typography>
            {ret.buyerShippedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                · ارسال: {new Date(ret.buyerShippedAt).toLocaleDateString('fa-IR')}
              </Typography>
            )}
          </Box>
        )}

        {ret.buyerShipDeadline && !ret.sellerReceivedAt && (
          <Box>
            <Typography variant="caption" color="text.secondary">مهلت ارسال خریدار:</Typography>{' '}
            <Typography variant="caption">
              {new Date(ret.buyerShipDeadline).toLocaleDateString('fa-IR')}
            </Typography>
          </Box>
        )}

        {ret.sellerReceivedAt && (
          <Box>
            <Typography variant="caption" color="text.secondary">تأیید توسط فروشنده در:</Typography>{' '}
            <Typography variant="caption">
              {new Date(ret.sellerReceivedAt).toLocaleDateString('fa-IR')}
            </Typography>
            {ret.sellerReceiveNotes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                یادداشت: {ret.sellerReceiveNotes}
              </Typography>
            )}
          </Box>
        )}

        {ret.rejectedAt && (
          <Box>
            <Typography variant="caption" color="text.secondary">رد توسط فروشنده در:</Typography>{' '}
            <Typography variant="caption" color="error.main">
              {new Date(ret.rejectedAt).toLocaleDateString('fa-IR')}
            </Typography>
            {ret.rejectionReason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                دلیل: {ret.rejectionReason}
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      <Alert severity={meta.color === 'success' ? 'success' : meta.color === 'error' ? 'error' : 'info'} sx={{ mt: 1.5, fontSize: '0.8rem' }}>
        {meta.hint}
      </Alert>

      {state.canForceRelease && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              اگر فروشنده پاسخگو نیست یا بدون دلیل موجه دریافت را رد کرده، می‌توانید قفل را به‌صورت اجباری آزاد کنید.
              این عملیات مبلغ بازپرداخت را برای برداشت توسط خریدار آزاد می‌کند.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<LockOpenIcon />}
              onClick={() => setForceDialogOpen(true)}
            >
              آزادسازی اجباری قفل
            </Button>
          </Box>
        </>
      )}

      <Dialog open={forceDialogOpen} onClose={() => !forcing && setForceDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>آزادسازی اجباری قفل بازپرداخت</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            با تأیید، قفل بازپرداخت <strong>{fa(ret.refundAmount || 0)} تومان</strong> در کیف پول خریدار آزاد می‌شود
            و خریدار می‌تواند مبلغ را برداشت کند. این عملیات قابل برگشت نیست.
          </Typography>
          <TextField
            size="small"
            label="یادداشت ادمین (اختیاری)"
            value={forceNotes}
            onChange={e => setForceNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="مثال: کد رهگیری معتبر است، فروشنده پاسخگو نیست"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceDialogOpen(false)} disabled={forcing}>
            انصراف
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleForceRelease}
            disabled={forcing}
            startIcon={forcing ? <CircularProgress size={16} /> : <LockOpenIcon />}
          >
            {forcing ? 'در حال آزادسازی...' : 'تأیید آزادسازی'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminPostalReturnPanel;
